import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The suite's primitives.
 *
 * They are deliberately thin wrappers over real elements that set a class
 * name and nothing else: three portals have to look like one product, and
 * the cheapest way to guarantee that is for every button in all three to be
 * the same `<button className="esm-button ...">`. Anything that needs a
 * behaviour (a form field's error wiring, a table's empty state) is here
 * because forgetting it is a bug a designer never sees but a screen reader
 * user does.
 */
function cx(...parts) {
    return parts.filter(Boolean).join(" ");
}
export function Button({ variant = "primary", busy = false, className, children, ...rest }) {
    return (_jsxs("button", { ...rest, className: cx("esm-button", `esm-button--${variant}`, className), disabled: rest.disabled || busy, "aria-busy": busy || undefined, children: [busy ? _jsx(Spinner, { inline: true }) : null, children] }));
}
/** Field is label + control + hint + error, wired together. */
export function Field({ label, htmlFor, hint, error, children }) {
    return (_jsxs("div", { className: cx("esm-field", error && "esm-field--invalid"), children: [_jsx("label", { className: "esm-field__label", htmlFor: htmlFor, children: label }), children, hint && !error ? (_jsx("p", { className: "esm-field__hint", id: `${htmlFor}-hint`, children: hint })) : null, error ? (
            // role="alert" so a validation failure is announced when it appears
            // rather than only when the field is next focused.
            _jsx("p", { className: "esm-field__error", id: `${htmlFor}-error`, role: "alert", children: error })) : null] }));
}
export function Input({ className, invalid, ...rest }) {
    return (_jsx("input", { ...rest, className: cx("esm-input", className), "aria-invalid": invalid || undefined, "aria-describedby": invalid && rest.id ? `${rest.id}-error` : rest["aria-describedby"] }));
}
export function Select({ className, ...rest }) {
    return _jsx("select", { ...rest, className: cx("esm-input", "esm-select", className) });
}
export function Card({ title, actions, className, children, ...rest }) {
    return (_jsxs("section", { ...rest, className: cx("esm-card", className), children: [title || actions ? (_jsxs("header", { className: "esm-card__header", children: [title ? _jsx("h2", { className: "esm-card__title", children: title }) : null, actions ? _jsx("div", { className: "esm-card__actions", children: actions }) : null] })) : null, _jsx("div", { className: "esm-card__body", children: children })] }));
}
export function Alert({ tone = "info", title, children }) {
    return (_jsxs("div", { className: cx("esm-alert", `esm-alert--${tone}`), role: tone === "danger" ? "alert" : "status", children: [title ? _jsx("strong", { className: "esm-alert__title", children: title }) : null, children ? _jsx("div", { className: "esm-alert__body", children: children }) : null] }));
}
export function Badge({ tone = "neutral", children }) {
    return _jsx("span", { className: cx("esm-badge", `esm-badge--${tone}`), children: children });
}
export function Spinner({ inline = false, label = "Loading" }) {
    return (_jsx("span", { className: cx("esm-spinner", inline && "esm-spinner--inline"), role: "status", "aria-label": label }));
}
export function PageHeader({ title, description, actions }) {
    return (_jsxs("header", { className: "esm-page-header", children: [_jsxs("div", { children: [_jsx("h1", { className: "esm-page-header__title", children: title }), description ? _jsx("p", { className: "esm-page-header__description", children: description }) : null] }), actions ? _jsx("div", { className: "esm-page-header__actions", children: actions }) : null] }));
}
export function DataTable({ columns, rows, rowKey, empty = "Nothing here yet." }) {
    if (rows.length === 0) {
        return _jsx("p", { className: "esm-table__empty", children: empty });
    }
    return (
    // The scroll container is the table's own, not the card's.
    //
    // `.esm-card` sets `overflow: hidden` because a rounded corner has to clip
    // something, so a table wider than its card was being CUT OFF — no
    // scrollbar, no edge shadow, and no indication that further columns
    // existed. Scrolling `.esm-card__body` instead would have worked and would
    // have dragged the card's header, alerts and forms sideways along with the
    // columns; the table is the only child that legitimately does not fit, so
    // it is the only one that moves.
    _jsx("div", { className: "esm-table-scroll", children: _jsxs("table", { className: "esm-table", children: [_jsx("thead", { children: _jsx("tr", { children: columns.map((c) => (_jsx("th", { className: cx(c.align === "end" && "esm-table--end"), scope: "col", children: c.header }, c.key))) }) }), _jsx("tbody", { children: rows.map((row) => (_jsx("tr", { children: columns.map((c) => (_jsx("td", { className: cx(c.align === "end" && "esm-table--end"), children: c.render(row) }, c.key))) }, rowKey(row)))) })] }) }));
}
export function EmptyState({ title, children }) {
    return (_jsxs("div", { className: "esm-empty", children: [_jsx("h3", { className: "esm-empty__title", children: title }), children ? _jsx("div", { className: "esm-empty__body", children: children }) : null] }));
}
//# sourceMappingURL=primitives.js.map